import React, { useEffect, useState } from "react";
import {
  Paper,
  Avatar,
  Button,
  Chip,
  Divider,
  Grid,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
} from "@mui/material";
import {
  Edit as EditIcon,
  Storefront as StorefrontIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  AccountBalance as BankIcon,
  Home as HomeIcon,
  VerifiedUser as VerifiedIcon,
} from "@mui/icons-material";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useAppDispatch, useAppSelector } from "../../../State/Store";
import { fetchSellerProfile, updateSellerProfile } from "../../../State/seller/SellerSlice";

const validationSchema = Yup.object({
  sellerName: Yup.string().required("Seller name is required"),
  mobile: Yup.string()
    .matches(/^(?:254|\+254|0)?([17][0-9]{8})$/, "Enter a valid Kenyan phone number (e.g., 0712345678)")
    .required("Mobile number is required"),
  businessDetails: Yup.object({
    businessName: Yup.string().required("Business name is required"),
    gstin: Yup.string()
      .matches(/^[A-Z][0-9]{9}[A-Z]$/, "Enter a valid KRA PIN (e.g., P051234567X)")
      .required("KRA PIN is required"),
  }),
  pickupAddress: Yup.object({
    name: Yup.string().required("Contact name is required"),
    locality: Yup.string().required("Area/Estate/Locality is required"),
    address: Yup.string().required("Street address / Building is required"),
    city: Yup.string().required("Town/City is required"),
    state: Yup.string().required("County is required"),
    pinCode: Yup.string().required("Postal code is required"),
    mobile: Yup.string().required("Pickup mobile is required"),
  }),
  bankDetails: Yup.object({
    accountHolderName: Yup.string().required("Account holder name is required"),
    accountNumber: Yup.string().required("Account number is required"),
    ifscCode: Yup.string().required("Bank Branch Code / SWIFT is required"),
  }),
});

const Profile = () => {
  const dispatch = useAppDispatch();
  const { profile: seller, loading, error } = useAppSelector((store: any) => store.seller || {});

  const [openEditModal, setOpenEditModal] = useState(false);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  useEffect(() => {
    const jwt = localStorage.getItem("jwt");
    if (jwt) {
      dispatch(fetchSellerProfile(jwt));
    }
  }, [dispatch]);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      sellerName: seller?.sellerName || "",
      email: seller?.email || "",
      mobile: seller?.mobile || "",
      businessDetails: {
        businessName: seller?.businessDetails?.businessName || "",
        gstin: seller?.businessDetails?.gstin || "",
      },
      pickupAddress: {
        name: seller?.pickupAddress?.name || "",
        locality: seller?.pickupAddress?.locality || "",
        address: seller?.pickupAddress?.address || "",
        city: seller?.pickupAddress?.city || "",
        state: seller?.pickupAddress?.state || "",
        pinCode: seller?.pickupAddress?.pinCode || "",
        mobile: seller?.pickupAddress?.mobile || "",
      },
      bankDetails: {
        accountHolderName: seller?.bankDetails?.accountHolderName || "",
        accountNumber: seller?.bankDetails?.accountNumber || "",
        ifscCode: seller?.bankDetails?.ifscCode || "",
      },
    },
    validationSchema,
    onSubmit: async (values, { setSubmitting }) => {
      try {
        const jwt = localStorage.getItem("jwt") || "";
        console.log("Submitting updated seller profile:", values);
        if (typeof updateSellerProfile === "function") {
          await dispatch(updateSellerProfile({ jwt, profileData: values })).unwrap();
        }
        setSuccessMsg("Profile updated successfully!");
        setOpenEditModal(false);
        setTimeout(() => setSuccessMsg(null), 4000);
      } catch (err: any) {
        console.error("Failed to update seller profile:", err);
      } finally {
        setSubmitting(false);
      }
    },
  });

  if (loading && !seller) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <CircularProgress />
      </div>
    );
  }

  const businessName = seller?.businessDetails?.businessName || seller?.sellerName || "Seller Store";
  const accountStatus = seller?.accountStatus || "PENDING_VERIFICATION";

  return (
    <div className="max-w-6xl mx-auto p-4 sm:p-6 space-y-6">
      {/* HEADER BANNER */}
      <Paper elevation={2} className="p-6 bg-gradient-to-r from-slate-900 to-indigo-900 text-white rounded-xl shadow-lg">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div className="flex items-center gap-4">
            <Avatar className="w-20 h-20 bg-indigo-600 text-3xl font-bold border-2 border-white shadow">
              {businessName.charAt(0).toUpperCase()}
            </Avatar>
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-2xl font-bold">{businessName}</h1>
                {accountStatus === "ACTIVE" && (
                  <Chip
                    icon={<VerifiedIcon style={{ color: "#4caf50" }} />}
                    label="Verified Seller"
                    size="small"
                    className="bg-green-100 text-green-800 font-semibold"
                  />
                )}
              </div>
              <p className="text-indigo-200 text-sm mt-1">Owner: {seller?.sellerName || "N/A"}</p>
              <p className="text-indigo-300 text-xs mt-0.5">Email: {seller?.email}</p>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 w-full md:w-auto">
            <Chip
              label={`Status: ${accountStatus}`}
              color={accountStatus === "ACTIVE" ? "success" : "warning"}
              variant="filled"
              className="font-bold uppercase tracking-wider"
            />
            <Button
              variant="contained"
              startIcon={<EditIcon />}
              onClick={() => setOpenEditModal(true)}
              className="bg-indigo-600 hover:bg-indigo-700 text-white font-semibold"
            >
              Edit Profile
            </Button>
          </div>
        </div>
      </Paper>

      {successMsg && <Alert severity="success">{successMsg}</Alert>}
      {error && <Alert severity="error">{error}</Alert>}

      {/* MAIN GRID */}
      <Grid container spacing={3}>
        {/* Business Details */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper className="p-6 rounded-xl shadow-sm border border-slate-200 h-full space-y-4">
            <div className="flex items-center gap-2 border-b pb-3">
              <StorefrontIcon className="text-indigo-600" />
              <h2 className="text-lg font-bold text-slate-800">Business Details</h2>
            </div>

            <div className="space-y-3 text-sm text-slate-700">
              <div className="flex items-center justify-between py-1 border-b border-slate-100">
                <span className="text-slate-500 font-medium">Business Name</span>
                <span className="font-semibold">{seller?.businessDetails?.businessName || "N/A"}</span>
              </div>
              <div className="flex items-center justify-between py-1 border-b border-slate-100">
                <span className="text-slate-500 font-medium">Seller Name</span>
                <span className="font-semibold">{seller?.sellerName || "N/A"}</span>
              </div>
              <div className="flex items-center justify-between py-1 border-b border-slate-100">
                <span className="text-slate-500 font-medium">Email</span>
                <span className="font-semibold flex items-center gap-1">
                  <EmailIcon fontSize="small" className="text-slate-400" />
                  {seller?.email || "N/A"}
                </span>
              </div>
              <div className="flex items-center justify-between py-1 border-b border-slate-100">
                <span className="text-slate-500 font-medium">Mobile</span>
                <span className="font-semibold flex items-center gap-1">
                  <PhoneIcon fontSize="small" className="text-slate-400" />
                  {seller?.mobile || "N/A"}
                </span>
              </div>
              <div className="flex items-center justify-between py-1">
                <span className="text-slate-500 font-medium">KRA PIN</span>
                <span className="font-mono bg-slate-100 px-2 py-0.5 rounded font-semibold text-slate-800">
                  {seller?.businessDetails?.gstin || "N/A"}
                </span>
              </div>
            </div>
          </Paper>
        </Grid>

        {/* Bank Details */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper className="p-6 rounded-xl shadow-sm border border-slate-200 h-full space-y-4">
            <div className="flex items-center gap-2 border-b pb-3">
              <BankIcon className="text-indigo-600" />
              <h2 className="text-lg font-bold text-slate-800">Bank & Payout Account</h2>
            </div>

            <div className="space-y-3 text-sm text-slate-700">
              <div className="flex items-center justify-between py-1 border-b border-slate-100">
                <span className="text-slate-500 font-medium">Account Holder</span>
                <span className="font-semibold">{seller?.bankDetails?.accountHolderName || "N/A"}</span>
              </div>
              <div className="flex items-center justify-between py-1 border-b border-slate-100">
                <span className="text-slate-500 font-medium">Account Number</span>
                <span className="font-mono font-semibold">
                  {seller?.bankDetails?.accountNumber
                    ? `•••• •••• ${seller.bankDetails.accountNumber.slice(-4)}`
                    : "N/A"}
                </span>
              </div>
              <div className="flex items-center justify-between py-1">
                <span className="text-slate-500 font-medium">Bank Branch Code / SWIFT</span>
                <span className="font-mono bg-slate-100 px-2 py-0.5 rounded font-semibold text-slate-800">
                  {seller?.bankDetails?.ifscCode || "N/A"}
                </span>
              </div>
            </div>
          </Paper>
        </Grid>

        {/* Pickup Address */}
        <Grid size={{ xs: 12 }}>
          <Paper className="p-6 rounded-xl shadow-sm border border-slate-200 space-y-4">
            <div className="flex items-center gap-2 border-b pb-3">
              <HomeIcon className="text-indigo-600" />
              <h2 className="text-lg font-bold text-slate-800">Warehouse & Pickup Address</h2>
            </div>

            {seller?.pickupAddress ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-slate-700">
                <div>
                  <p className="text-slate-500 font-medium">Contact Person</p>
                  <p className="font-semibold text-slate-800">{seller.pickupAddress.name || "N/A"}</p>
                </div>
                <div>
                  <p className="text-slate-500 font-medium">Contact Mobile</p>
                  <p className="font-semibold text-slate-800">{seller.pickupAddress.mobile || "N/A"}</p>
                </div>
                <div className="md:col-span-2">
                  <p className="text-slate-500 font-medium">Full Address</p>
                  <p className="font-semibold text-slate-800">
                    {seller.pickupAddress.address}, {seller.pickupAddress.locality},{" "}
                    {seller.pickupAddress.city}, County: {seller.pickupAddress.state} -{" "}
                    {seller.pickupAddress.pinCode}
                  </p>
                </div>
              </div>
            ) : (
              <p className="text-slate-500 italic">No pickup address configured.</p>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* EDIT MODAL */}
      <Dialog
        open={openEditModal}
        onClose={() => setOpenEditModal(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle className="font-bold text-slate-800 border-b">
          Edit Seller Profile
        </DialogTitle>
        <form onSubmit={formik.handleSubmit}>
          <DialogContent className="space-y-6 py-6">
            {/* SECTION 1: BUSINESS & KRA PIN */}
            <div>
              <h3 className="text-md font-bold text-indigo-700 mb-3">1. Business Information</h3>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Seller Name"
                    name="sellerName"
                    value={formik.values.sellerName}
                    onChange={formik.handleChange}
                    error={formik.touched.sellerName && Boolean(formik.errors.sellerName)}
                    helperText={formik.touched.sellerName && (formik.errors.sellerName as string)}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Business Name"
                    name="businessDetails.businessName"
                    value={formik.values.businessDetails.businessName}
                    onChange={formik.handleChange}
                    error={
                      Boolean(formik.touched.businessDetails?.businessName) &&
                      Boolean(formik.errors.businessDetails?.businessName)
                    }
                    helperText={
                      formik.touched.businessDetails?.businessName &&
                      (formik.errors.businessDetails?.businessName as string)
                    }
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Mobile Number (e.g. 0712345678)"
                    name="mobile"
                    value={formik.values.mobile}
                    onChange={formik.handleChange}
                    error={formik.touched.mobile && Boolean(formik.errors.mobile)}
                    helperText={formik.touched.mobile && (formik.errors.mobile as string)}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    size="small"
                    label="KRA PIN (e.g. P051234567X)"
                    name="businessDetails.gstin"
                    value={formik.values.businessDetails.gstin}
                    onChange={formik.handleChange}
                    placeholder="P0XXXXXXXXX"
                    error={
                      Boolean(formik.touched.businessDetails?.gstin) &&
                      Boolean(formik.errors.businessDetails?.gstin)
                    }
                    helperText={
                      formik.touched.businessDetails?.gstin &&
                      (formik.errors.businessDetails?.gstin as string)
                    }
                  />
                </Grid>
              </Grid>
            </div>

            <Divider />

            {/* SECTION 2: BANK OR PAYOUT DETAILS */}
            <div>
              <h3 className="text-md font-bold text-indigo-700 mb-3">2. Bank & Payout Details</h3>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Account Holder Name"
                    name="bankDetails.accountHolderName"
                    value={formik.values.bankDetails.accountHolderName}
                    onChange={formik.handleChange}
                    error={
                      Boolean(formik.touched.bankDetails?.accountHolderName) &&
                      Boolean(formik.errors.bankDetails?.accountHolderName)
                    }
                    helperText={
                      formik.touched.bankDetails?.accountHolderName &&
                      (formik.errors.bankDetails?.accountHolderName as string)
                    }
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Account Number / Paybill"
                    name="bankDetails.accountNumber"
                    value={formik.values.bankDetails.accountNumber}
                    onChange={formik.handleChange}
                    error={
                      Boolean(formik.touched.bankDetails?.accountNumber) &&
                      Boolean(formik.errors.bankDetails?.accountNumber)
                    }
                    helperText={
                      formik.touched.bankDetails?.accountNumber &&
                      (formik.errors.bankDetails?.accountNumber as string)
                    }
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Bank Branch Code / SWIFT"
                    name="bankDetails.ifscCode"
                    value={formik.values.bankDetails.ifscCode}
                    onChange={formik.handleChange}
                    error={
                      Boolean(formik.touched.bankDetails?.ifscCode) &&
                      Boolean(formik.errors.bankDetails?.ifscCode)
                    }
                    helperText={
                      formik.touched.bankDetails?.ifscCode &&
                      (formik.errors.bankDetails?.ifscCode as string)
                    }
                  />
                </Grid>
              </Grid>
            </div>

            <Divider />

            {/* SECTION 3: PICKUP / WAREHOUSE ADDRESS */}
            <div>
              <h3 className="text-md font-bold text-indigo-700 mb-3">3. Warehouse & Pickup Address</h3>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Contact Person Name"
                    name="pickupAddress.name"
                    value={formik.values.pickupAddress.name}
                    onChange={formik.handleChange}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Contact Mobile"
                    name="pickupAddress.mobile"
                    value={formik.values.pickupAddress.mobile}
                    onChange={formik.handleChange}
                  />
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Street Address / Building / Stall No."
                    name="pickupAddress.address"
                    value={formik.values.pickupAddress.address}
                    onChange={formik.handleChange}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Locality / Area / Estate (e.g. Westlands)"
                    name="pickupAddress.locality"
                    value={formik.values.pickupAddress.locality}
                    onChange={formik.handleChange}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Town / City"
                    name="pickupAddress.city"
                    value={formik.values.pickupAddress.city}
                    onChange={formik.handleChange}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    size="small"
                    label="County (e.g. Nairobi, Mombasa)"
                    name="pickupAddress.state"
                    value={formik.values.pickupAddress.state}
                    onChange={formik.handleChange}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Postal Code (e.g. 00100)"
                    name="pickupAddress.pinCode"
                    value={formik.values.pickupAddress.pinCode}
                    onChange={formik.handleChange}
                  />
                </Grid>
              </Grid>
            </div>
          </DialogContent>

          <DialogActions className="p-4 bg-slate-50 border-t">
            <Button onClick={() => setOpenEditModal(false)} color="inherit">
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              className="bg-indigo-600 hover:bg-indigo-700 text-white"
            >
              Save Changes
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </div>
  );
};

export default Profile;